/**
 * Homepage hero — up to three full-bleed slides, crossfaded.
 *
 * Replaces the Glide-based home-carousel.js for the 2026 homepage: no library,
 * autoplay with progress bars, arrows, dots, swipe, arrow keys, and pauses on
 * hover, focus and tab-hidden. Honours prefers-reduced-motion.
 *
 * No-ops when .js-homehero is absent.
 */

const DURATION = 7000;

export default function homeHero() {
  const hero = document.querySelector('.js-homehero');
  if (!hero) return;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const slides = Array.from(hero.querySelectorAll('.homehero__slide'));
  const dots = Array.from(hero.querySelectorAll('.homehero__dot'));
  const live = hero.querySelector('.js-homehero-live');
  if (slides.length === 0) return;

  hero.style.setProperty('--homehero-dur', `${DURATION}ms`);

  let index = 0;
  let timer = null;

  function show(next, announce) {
    index = (next + slides.length) % slides.length;

    slides.forEach((s, i) => {
      const active = i === index;
      s.classList.toggle('is-active', active);
      s.setAttribute('aria-hidden', String(!active));
      // Keep off-screen slides out of the tab order.
      s.querySelectorAll('a, button').forEach((el) => {
        if (active) el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', '-1');
      });
    });

    dots.forEach((d, i) => {
      // Restart the fill animation on the newly active dot.
      d.classList.remove('is-active');
      void d.offsetWidth;
      d.classList.toggle('is-active', i === index);
      d.setAttribute('aria-selected', String(i === index));
    });

    if (announce && live) {
      const title = slides[index].querySelector('.homehero__title');
      live.textContent = `Slide ${index + 1} of ${slides.length}: ${title ? title.textContent : ''}`;
    }
  }

  function stop() {
    if (timer) { window.clearInterval(timer); timer = null; }
    hero.classList.add('is-paused');
  }

  function play() {
    if (reduce || slides.length < 2) return;
    stop();
    timer = window.setInterval(() => show(index + 1), DURATION);
    hero.classList.remove('is-paused');
  }

  const next = hero.querySelector('.homehero__arrow--next');
  const prev = hero.querySelector('.homehero__arrow--prev');
  if (next) next.addEventListener('click', () => { show(index + 1, true); play(); });
  if (prev) prev.addEventListener('click', () => { show(index - 1, true); play(); });
  dots.forEach((d, i) => d.addEventListener('click', () => { show(i, true); play(); }));

  hero.addEventListener('mouseenter', stop);
  hero.addEventListener('mouseleave', play);
  hero.addEventListener('focusin', stop);
  hero.addEventListener('focusout', (e) => {
    if (!hero.contains(e.relatedTarget)) play();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stop(); else play();
  });

  hero.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight') { show(index + 1, true); play(); }
    if (e.key === 'ArrowLeft') { show(index - 1, true); play(); }
  });

  let startX = null;
  hero.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
  hero.addEventListener('touchend', (e) => {
    if (startX === null) return;
    const dx = e.changedTouches[0].clientX - startX;
    if (Math.abs(dx) > 55) { show(index + (dx < 0 ? 1 : -1), true); play(); }
    startX = null;
  });

  show(0);
  play();
}
